import { DbSongWithIssues, DbPlaylist } from './dataClient';
import { StoryCard, Playlist } from './types';
import { Song } from './songDataClient';
import { getAlbumArtUrl } from './mediaHelpers';

/**
 * Transform a database song to a StoryCard for the UI
 */
export function transformSongToCard(song: DbSongWithIssues): StoryCard {
  const primaryIssue = song.issues[0];
  const secondaryIssues = song.issues.slice(1, 4);

  return {
    id: song.id,
    songTitle: song.title,
    artistName: song.artist,
    albumArtUrl: getAlbumArtUrl(song),
    broadCategory: primaryIssue?.label || 'Justice',
    primaryIssueTag: primaryIssue?.label || 'Justice',
    secondaryIssueTags: secondaryIssues.map(i => i.label),
    tags: song.issues.map(i => i.label),
    subTags: song.issues.map(i => i.slug),
    issueTags: song.issues.map(i => i.slug),
    hookText: song.description || 'Explore this story',
    storyContent: song.story_body || song.description || '',
    deepInsight: song.deep_insight || undefined,
    quickFact: song.quick_fact || undefined,
    lyricHighlight: song.lyric_spotlight || undefined,
    timeline: song.timeline_json || undefined,
    contextInTime: song.context_in_time || undefined,
    spotifyUrl: song.spotify_url || undefined,
    appleMusicUrl: song.apple_music_url || undefined,
    youtubeUrl: song.youtube_url || undefined,
    visualUrl: undefined,
    visualCaption: undefined,
    heroSubline: undefined,
    order: 0,
  };
}

/**
 * Transform a Song from songDataClient to a StoryCard for the UI
 */
export function transformSongDataToCard(song: Song): StoryCard {
  const tagsToUse = song.primary_tags && song.primary_tags.length > 0 ? song.primary_tags : song.tags;
  const primaryTag = tagsToUse?.[0] || 'Justice';
  const secondaryTags = tagsToUse?.slice(1, 4) || [];

  return {
    id: song.id,
    songTitle: song.title,
    artistName: song.artist,
    albumArtUrl: getAlbumArtUrl(song),
    broadCategory: primaryTag,
    primaryIssueTag: primaryTag,
    secondaryIssueTags: secondaryTags,
    tags: tagsToUse || [],
    subTags: tagsToUse || [],
    issueTags: tagsToUse || [],
    hookText: song.description || 'Explore this story',
    storyContent: song.story_body || song.description || '',
    deepInsight: song.deep_insight || undefined,
    quickFact: song.quick_fact || undefined,
    lyricHighlight: song.lyric_spotlight || undefined,
    timeline: song.timeline_json || undefined,
    contextInTime: song.context_in_time || undefined,
    spotifyUrl: song.spotify_url || undefined,
    appleMusicUrl: song.apple_music_url || undefined,
    youtubeUrl: song.youtube_url || undefined,
    visualUrl: undefined,
    visualCaption: undefined,
    heroSubline: undefined,
    order: 0,
  };
}

/**
 * Transform multiple database songs to StoryCards
 */
export function transformSongsToCards(songs: DbSongWithIssues[]): StoryCard[] {
  return songs.map(transformSongToCard);
}

/**
 * Transform multiple Songs from songDataClient to StoryCards
 */
export function transformSongDataToCards(songs: Song[]): StoryCard[] {
  return songs.map(transformSongDataToCard);
}

/**
 * Transform a database playlist to a Playlist for the UI
 */
export function transformPlaylistToUI(playlist: DbPlaylist): Playlist {
  return {
    id: playlist.id,
    name: playlist.name,
    slug: playlist.slug || '',
    description: playlist.description || '',
    coverImageUrl: '/placeholder-civicbeat.svg',
    songIds: [],
    isSaved: false,
    order: 0,
  };
}

/**
 * Transform multiple database playlists to UI Playlists
 */
export function transformPlaylistsToUI(playlists: DbPlaylist[]): Playlist[] {
  return playlists.map(transformPlaylistToUI);
}
